/**
 * The dose-limiting toxicity (DLT) assessment form asks which of the PROTOCOL's
 * own DLT criteria were met (Jim, 2026-10-17).
 *
 * The deterministic pass does ONE thing: it ensures the criteria checklist
 * (variable DLTCRIT) exists and carries analysis.dlt.criteria verbatim. It
 * never removes, replaces or annotates any other question: a decision made
 * from a question's wording leaks (measured: it removed and replaced real
 * questions). Everything else goes to the model as facts in the prompt.
 *
 * Source of truth: analysis.dlt (agents/dltFacts.ts). Nothing here invents a
 * criterion: with no stated definition the question carries a note, not options.
 */
import { normaliseDlt, type DltFacts } from "../agents/dltFacts";
import type { AuthVariable } from "./crfProtocolReading";

export const DLT_CHECKLIST_VAR = "DLTCRIT";
export const DLT_OTHER_VAR = "DLTCRITSP";
export const DLT_CHECKLIST_TEXT = "Which protocol DLT criteria were met?";
export const DLT_NO_CRITERIA_NOTE = "[SPONSOR REVIEW: the protocol analysis holds no DLT criteria; define the criteria for this question]";
const OTHER_LABEL = "Other, specify";

export interface DltChange {
  varName: string;
  action: "checklist_added" | "checklist_updated" | "other_field_added";
  why: string;
}

export interface DltContext {
  analysis: Record<string, unknown> | null;
  formName?: string;
}

const DLT_WORDS = /\bDLTs?\b|dose[- ]limiting/i;

/**
 * A form that IS a DLT assessment: the WHOLE name (lowercased, punctuation collapsed) is the DLT phrase,
 * optionally followed by form-type words. "Vital Signs (DLT window) Log" or "Demographics Form (DLT evaluable)"
 * merely mention DLTs and are not it. "Review", "follow up" and "committee" stay out: they name a different
 * form (a committee's review, a later visit).
 *
 * The study-level coverage rule (schedule/scheduleCoverage.ts, dltUnit) counts a DLT form by this same test,
 * so a name that fails here gets a duplicate DLT form added AND misses its checklist. The form-type words
 * include the ones a model plausibly gives a DLT form ("Dose-Limiting Toxicity Monitoring", "DLT Observation",
 * "DLT Event Form"), 2026-10-17, singular or plural ("DLT Assessments"). No "Cycle 1" allowance: deferred.
 */
const DLT_FORM_NAME = /^(?:dose limiting toxicit(?:y|ies)(?: dlts?)?|dlts?)(?: (?:assessments?|evaluations?|forms?|reports?|reporting|records?|crf|logs?|monitoring|observations?|determination|checklist|worksheet|events?))*$/;
export function isDltForm(formName?: string | null): boolean {
  const n = String(formName ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return DLT_FORM_NAME.test(n);
}

const MAX_RULES = 20;
const MAX_RULE_CHARS = 2000;

/** One entry's text: a string as it is; an object's string fields, in order. */
const entryText = (x: unknown): string => {
  if (typeof x === "string") return x.trim();
  if (x && typeof x === "object") {
    return Object.values(x as Record<string, unknown>).filter((v): v is string => typeof v === "string").map((v) => v.trim()).filter(Boolean).join(" ");
  }
  return "";
};

/** The protocol's dose-modification entries, exactly as the analysis holds them. */
export function doseModificationRules(analysis: Record<string, unknown> | null | undefined): string[] {
  const v = analysis?.dosageModifications;
  return Array.isArray(v) ? v.map(entryText).filter(Boolean) : [];
}

/** The dose-modification rules as a fact for a prompt: at most 20 entries and 2000 characters. Empty when none. */
export function doseRulesFact(rulesIn: string[]): string {
  const rules = rulesIn.map((r) => r.trim()).filter(Boolean);
  if (rules.length === 0) return "";
  const kept: string[] = [];
  let used = 0;
  for (const r of rules.slice(0, MAX_RULES)) {
    if (used + r.length > MAX_RULE_CHARS) {
      // A first rule longer than the cap is cut at a word boundary, so at least one rule is always listed.
      if (kept.length === 0) {
        const cut = r.slice(0, MAX_RULE_CHARS - 1);
        const at = cut.lastIndexOf(" ");
        kept.push(`${(at > 0 ? cut.slice(0, at) : cut).trimEnd()}…`);
      }
      break;
    }
    kept.push(r);
    used += r.length;
  }
  const truncated = kept.length < rules.length || kept[0].endsWith("…") && kept[0] !== rules[0];
  const more = truncated ? " (further rules are in the protocol)" : "";
  return `The protocol's dose-modification rules, as written: ${kept.map((r, i) => `${i + 1}. ${r}`).join(" ")}${more} Follow them, and do not assume a dose reduction or a dose delay that they do not allow.`;
}

const factsOf = (analysis: Record<string, unknown> | null | undefined): DltFacts => normaliseDlt(analysis?.dlt);

/** The short instruction the form-writing prompt carries on a DLT form. Empty off a DLT form. */
export function dltPromptBlock(ctx: DltContext): string {
  if (!isDltForm(ctx.formName)) return "";
  const f = factsOf(ctx.analysis);
  const lines: string[] = [];
  if (f.stated) {
    lines.push(`The protocol defines these DLT criteria: ${f.criteria.map((c, i) => `${i + 1}. ${c.text}`).join(" ")}`);
    lines.push(`Use exactly these criteria, and add no others. The criteria question is "${DLT_CHECKLIST_TEXT}" (variable ${DLT_CHECKLIST_VAR}).`);
    lines.push(f.delayCountsAsDlt === true ? "The protocol counts a dose delay or omission as a DLT." : "A dose delay or omission is a DLT criterion only if the protocol says so; this protocol analysis does not say so.");
    if (f.otherEventsMayCount === true) lines.push(`The protocol lets other toxicities be judged dose-limiting: allow "${OTHER_LABEL}".`);
  } else {
    lines.push(`The protocol analysis does not state DLT criteria. Do not invent any. The criteria question is "${DLT_CHECKLIST_TEXT}" (variable ${DLT_CHECKLIST_VAR}), with no options.`);
  }
  const rules = doseRulesFact(doseModificationRules(ctx.analysis));
  if (rules) lines.push(rules);
  return ["## THE DLT FORM (it outranks every default)", ...lines].join("\n");
}

export function applyDltForm<V extends AuthVariable>(variables: V[], ctx: DltContext): { variables: V[]; changes: DltChange[] } {
  if (!isDltForm(ctx.formName)) return { variables, changes: [] };
  const f = factsOf(ctx.analysis);
  const changes: DltChange[] = [];
  const options: Array<{ code: number; label: string }> = f.criteria.map((c, i) => ({ code: i + 1, label: c.text.trim() }));
  const withOther = f.stated && f.otherEventsMayCount === true;
  if (withOther) options.push({ code: options.length + 1, label: OTHER_LABEL });
  const codesFrom = f.stated ? "protocol DLT criteria" : "no DLT criteria in the protocol analysis";

  const list = variables.slice();
  const at = list.findIndex((v) => v.varName === DLT_CHECKLIST_VAR);
  if (at >= 0) {
    const old = list[at];
    const text = String(old.questionText ?? "");
    list[at] = {
      ...old,
      questionText: f.stated || /\[SPONSOR REVIEW/i.test(text) ? text : `${text.trim()} ${DLT_NO_CRITERIA_NOTE}`,
      responseType: "multi_select",
      options,
      required: true,
      codesFrom,
    } as V;
    changes.push({ varName: DLT_CHECKLIST_VAR, action: "checklist_updated", why: f.stated ? `options set to the ${f.criteria.length} protocol criteria` : "no DLT criteria in the protocol analysis: options emptied, sponsor-review note added" });
  } else {
    const checklist = {
      varName: DLT_CHECKLIST_VAR,
      questionText: f.stated ? DLT_CHECKLIST_TEXT : `${DLT_CHECKLIST_TEXT} ${DLT_NO_CRITERIA_NOTE}`,
      responseType: "multi_select",
      options,
      unit: "",
      required: true,
      codesFrom,
    } as unknown as V;
    const gate = list.findIndex((v) => String(v.responseType) === "yesno" && DLT_WORDS.test(String(v.questionText ?? "")));
    list.splice(gate >= 0 ? gate + 1 : list.length, 0, checklist);
    changes.push({ varName: DLT_CHECKLIST_VAR, action: "checklist_added", why: f.stated ? `added: ${f.criteria.length} protocol criteria${withOther ? " plus Other, specify" : ""}` : "added without options: the protocol analysis holds no DLT criteria" });
  }
  if (withOther && !list.some((v) => v.varName === DLT_OTHER_VAR)) {
    const i = list.findIndex((v) => v.varName === DLT_CHECKLIST_VAR);
    list.splice(i + 1, 0, { varName: DLT_OTHER_VAR, questionText: `If "${OTHER_LABEL}", describe the toxicity judged dose-limiting`, responseType: "text", options: [], unit: "" } as unknown as V);
    changes.push({ varName: DLT_OTHER_VAR, action: "other_field_added", why: "the protocol lets other toxicities be judged dose-limiting" });
  }
  return { variables: list, changes };
}
